import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { auth, analytics, functions, db } from '../../../firebase';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import ResetPasswordDialog from '../../../components/auth/ResetPasswordDialog';
import MfaDialog from '../../../components/auth/MfaDialog';
import InfoTooltip from '../../../components/InfoTooltip';
import useEmailPasswordSignIn from '../../../hooks/auth/useEmailPasswordSignIn';
import { doc, getDoc } from 'firebase/firestore';
import { sendLoginSummaryToSupport } from './functions/TriggerLoginSummaryEmail';
import { sanitizeEmailInput } from '../../../utils/inputSanitization';
import { getBackgroundGradient } from '../../../utils/environmentTheme';
import { getInformantDashboardPath } from '../../../utils/informantDashboardRouting';
import TermsPrivacyConsent from '../../../components/TermsPrivacyConsent';

function PatientSignUp() {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle success messages from URL parameters
  useEffect(() => {
    const message = searchParams.get('message');
    const encodedEmail = searchParams.get('e');
    const urlEmail = searchParams.get('email');
    
    if (message === 'setup_complete') {
      setSuccessMessage('Your account has been set up successfully! Please sign in with your new password.');
    } else if (message === 'account_already_setup') {
      setSuccessMessage('Your account is already set up. Please sign in with your password.');
    }

    // Email prefill is independent of the message type — it fires for any URL
    // carrying ?e= (base64) or ?email= (plain), including the new email-link flow
    // where no `message` param is present (e.g. "Account Setup Complete" email).
    if (encodedEmail) {
      try { setEmail(atob(encodedEmail)); } catch (_) {}
    } else if (urlEmail) { setEmail(decodeURIComponent(urlEmail)); }
  }, [searchParams]);
  // Centralized sign-in flow for patients
  const { isLoading: hookLoading, error: hookError, errorCode: hookErrorCode, mfaState, startSignIn, completeMfa, resetRecaptcha, cancelMfa, resendMfa } = useEmailPasswordSignIn({
    entryAccountType: 'Service User',
    onRoute: async (route, payload) => {
      if (route === 'verifyEmail') return; // patients typically sign in with verified email already
      if (route === 'passwordSetup') {
        const encodedEmail = encodeURIComponent(btoa(payload.email));
        return navigate(`/service-user/set-up-password?setup=true&e=${encodedEmail}`);
      }
      if (route === 'mfaSetup') return navigate('/service-user/set-up-mfa');
      if (route === 'dashboard') {
        try {
          const user = auth.currentUser;
          const idTokenResult = await user.getIdTokenResult(true);
          const moduleClaim = idTokenResult.claims.assessmentModule;

          if (idTokenResult.claims.userType === 'Informant') {
            return navigate(getInformantDashboardPath(idTokenResult.claims));
          }

          // Subcollection-based modules (Autism, future CYP) route to their own dashboard
          if (moduleClaim && moduleClaim !== 'adultAdhd') {
            sendLoginSummaryToSupport(user);
            return navigate('/service-user/modular-dashboard');
          }

          // ADHD pathway — preserve existing routing logic
          const userDoc = await getDoc(doc(db, 'patients', user.uid));
          const userData = userDoc.data();
          if (!userData?.hasSeenWelcome) return navigate('/service-user/welcome');
          sendLoginSummaryToSupport(user);
          return navigate('/patient-dashboard');
        } catch (_) {
          return navigate('/patient-dashboard');
        }
      }
    }
  });

  // Open MFA dialog when required by hook
  useEffect(() => {
    if (mfaState.required) setShowMFAPrompt(true);
  }, [mfaState.required]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    await startSignIn(email, password);
  };

  // MFA verification handled by passing the code directly to completeMfa(code)

  const handleForgotPasswordClose = () => setShowForgotPassword(false);
  const handleMFADialogClose = (open) => {
    setShowMFAPrompt(open);
    if (!open) resetRecaptcha();
  };

  // Ensure consistent vertical spacing at the top of the panel:
  // in dev/staging we render an environment banner that provides top space;
  // in production (no banner), we add top margin to the logo so it doesn't
  // touch the rounded top edge of the card.
  const showEnvBanner = (import.meta.env.VITE_ENVIRONMENT === "development" || import.meta.env.VITE_ENVIRONMENT === "staging");

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: getBackgroundGradient() }}>
      {/* Invisible reCAPTCHA container is mounted globally in public/index.html */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl drop-shadow-lg px-8 pb-8 mx-4">
        <div className="w-full">
          {showEnvBanner && (
            <div className={`-mx-8 mb-10 px-8 py-3 border font-semibold text-center rounded-t-2xl
              ${import.meta.env.VITE_ENVIRONMENT === "development" ? "bg-yellow-200 text-yellow-900 border-yellow-300" : "bg-blue-200 text-blue-900 border-blue-300"}
            `}>
              <div className="flex items-center justify-center gap-2">                
                <span>
                  {import.meta.env.VITE_ENVIRONMENT === "development" ? "Development Environment" : "Testing / Training Environment"}
                </span>
              </div>
            </div>
          )}
          <img src="/td_logo_full_2025.png" alt="Logo" className={`mx-auto mb-8 ${!showEnvBanner ? 'mt-10' : ''}`} style={{ width: '220px', height: 'auto' }} />
          <h2 className="text-2xl font-bold mb-2 text-center">Sign In Portal</h2>
          <p className="text-gray-600 mb-6 text-center">Please enter your details to access your account.</p>
          <form onSubmit={handleSignIn} 
          // autoComplete="on"
          >
            <div className="mb-4">
              <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Email</label>
                <InfoTooltip 
                  content={"Use the email address your healthcare provider registered for you. If you’re unsure, contact your healthcare provider"}
                  side="right"
                />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(sanitizeEmailInput(e.target.value))}
                required
                // autoComplete="username"
                placeholder="Enter your email"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
              />
            </div>
            <div className="mb-4">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Password</label>
                <InfoTooltip 
                  content={"Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"}
                  side="right"
                />
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  // autoComplete="current-password"
                  placeholder="Enter your password"
                  className="w-full px-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <span className="relative h-5 w-5">
                    <EyeIcon
                      className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                        showPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                      }`}
                    />
                    <EyeOffIcon
                      className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                        showPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                      }`}
                    />
                  </span>
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={hookLoading}
              className={`w-full py-2 px-4 rounded-md font-semibold text-white 
                  flex items-center justify-center mb-4 
                  ${hookLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90'
                }`}
            >
              {hookLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <TermsPrivacyConsent />
          </form>
          <div className={`transition-all duration-300 overflow-hidden ${successMessage ? 'max-h-24 mb-4' : 'max-h-0 mb-0'}`}>
            <div className={`bg-green-100 border border-green-400 text-green-700 px-4 py-3 text-sm rounded-lg relative ${successMessage ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} role="alert">
              <div className="flex items-start">
                <svg className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{successMessage}</span>
              </div>
            </div>
          </div>
          <div className={`transition-all duration-300 overflow-hidden ${error || hookError ? 'max-h-24' : 'max-h-0'}`}>
            <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error || hookError ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} role="alert">
              <span className="block sm:inline">{error || hookError}</span>
              {hookErrorCode && (
                <span className="block text-xs mt-1 text-gray-600">Error code: {hookErrorCode}</span>
              )}
            </div>
          </div>
        <p className="text-center mt-2">
          <button
            onClick={() => setShowForgotPassword(true)}
            className="text-blue-900 hover:text-blue-700 text-sm"
          >
            Forgot your password?
          </button>
        </p>
          <ResetPasswordDialog
            open={showForgotPassword}
            onOpenChange={handleForgotPasswordClose}
            initialEmail={email}
            onSubmit={async (toEmail) => {
              const sendPatientPasswordResetEmail = httpsCallable(functions, 'sendPatientPasswordResetEmail');
              await sendPatientPasswordResetEmail({ email: toEmail });
              logEvent(analytics, 'patient_password_reset_requested');
            }}
          />
          {/* Support footer for service users */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Need help? Contact your healthcare provider.
                      </p>
                    </div>
        </div>
      </div>
      <MfaDialog
        open={showMFAPrompt}
        onOpenChange={handleMFADialogClose}
        mfaType={mfaState?.mfaType || 'phone'}
        maskedPhone={mfaState?.maskedPhone}
        isLoading={hookLoading}
        error={error || hookError}
        errorCode={hookErrorCode}
        sentAt={mfaState.sentAt}
        cooldownSeconds={30}
        onVerify={async (code) => {
          await completeMfa(code);
        }}
        onCancel={cancelMfa}
        onResend={async () => await resendMfa()}
        tooltipSide="right"
        tooltipContent={"Having trouble receiving the code?\n\nDelays can happen due to your phone carrier or network issues.\n\nYou can try:\n\n→ Clearing your browser’s cache and trying again\n\n→ Using a different browser (like Chrome, Firefox, or Edge)\n\n→ Trying to log in using incognito/private browsing mode\n\nIf the problem continues, please contact your healthcare provider for help."}
      />
    </div>
  );
}

export default PatientSignUp;